<template>
  <Card class="mb-4">
    <template #title>
      <div class="flex w-full items-center">
        <div class="mr-auto">
          <span class="text-xl font-semibold">Tasks</span>
        </div>
        <div>
          <Button icon="pi pi-plus" label="Add Task" @click="openTaskDialog" />
        </div>
      </div>
    </template>
    <template #content>
      <div v-if="loading" class="flex justify-center p-4">
        <ProgressSpinner />
      </div>
      <div v-else-if="tasks.length === 0" class="text-center p-4 text-gray-500">No tasks found</div>
      <div v-else class="space-y-2">
        <div
          v-for="task in tasks"
          :key="task.id"
          class="p-3 border rounded-md hover:bg-gray-50"
          :class="{ 'cursor-pointer': props.useNav }"
          @click="props.useNav ? navigateToEntity(task) : undefined"
        >
          <div class="flex justify-between items-start">
            <div>
              <div class="font-medium">{{ task.title }}</div>
              <div v-if="task.description" class="text-sm text-gray-600 mt-1">
                {{ task.description }}
              </div>
              <div class="flex items-center mt-2 space-x-2">
                <Tag :value="task.status" :severity="getStatusSeverity(task.status)" />
                <span v-if="task.dueAt" class="text-sm text-gray-500">
                  Due: {{ formatDate(task.dueAt) }}
                </span>
              </div>
            </div>
            <div class="flex space-x-1">
              <Button
                icon="pi pi-pencil"
                class="p-button-text p-button-sm"
                @click.stop="editTask(task)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-text p-button-sm p-button-danger"
                @click.stop="confirmDeleteTask(task)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>

  <!-- Task Dialog -->
  <TaskFormDialog
    v-model:visible="taskDialogVisible"
    :is-editing="isEditing"
    :task="taskToEdit"
    @save="saveTask"
  />

  <!-- Delete Confirmation Dialog -->
  <Dialog
    v-model:visible="deleteDialogVisible"
    header="Confirm Delete"
    :style="{ width: '450px' }"
    :modal="true"
  >
    <div class="confirmation-content">
      <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
      <span>Are you sure you want to delete this task?</span>
    </div>
    <template #footer>
      <Button
        label="No"
        icon="pi pi-times"
        class="p-button-text"
        @click="deleteDialogVisible = false"
      />
      <Button label="Yes" icon="pi pi-check" class="p-button-danger" @click="deleteTask" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useTaskStore } from '@/stores/taskStore'
import { useRouter } from 'vue-router'
import type { Task } from '@/types/Task'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import 'v-calendar/style.css'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'
import { formatDate } from '@/utils/formatters'
import { useOrganizationStore } from '@/stores/v2/organization'
import { usePersonStore } from '@/stores/v2/person'
import TaskFormDialog from './TaskFormDialog.vue'
import { useTaskService } from '@/services/taskService'

const props = defineProps<{
  organizationId?: string
  personId?: string
  vehicleId?: string
  transactionId?: string
  assignedTo?: string
  status?: string | null
  dueDate?: string | null
  search?: string
  useNav?: boolean
}>()

const taskStore = useTaskStore()
const taskService = useTaskService()
const router = useRouter()
const organizationStore = useOrganizationStore()
const personStore = usePersonStore()

// State
const loading = ref(false)
const tasks = ref<Task[]>([])
const taskDialogVisible = ref(false)
const deleteDialogVisible = ref(false)
const isEditing = ref(false)
const taskToDelete = ref<Task | null>(null)
const taskToEdit = ref<Task | undefined>(undefined)

// Form state
const taskForm = ref<Partial<Task>>({
  title: '',
  description: '',
  status: 'todo',
  dueAt: undefined,
  assignedTo: undefined,
})

// Load tasks based on props
const loadTasks = async () => {
  loading.value = true
  try {
    await taskStore.load()

    // Apply filters based on props
    let filteredTasks = taskStore.items

    if (props.organizationId) {
      filteredTasks = taskStore.findBy((task) => task.organizationId === props.organizationId)
    } else if (props.personId) {
      filteredTasks = taskStore.findBy((task) => task.personId === props.personId)
    } else if (props.vehicleId) {
      filteredTasks = taskStore.findBy((task) => task.vehicleId === props.vehicleId)
    } else if (props.transactionId) {
      filteredTasks = taskStore.findBy((task) => task.transactionId === props.transactionId)
    } else if (props.assignedTo) {
      filteredTasks = taskStore.findBy((task) => task.assignedTo === props.assignedTo)
    }

    tasks.value = filteredTasks

    // Apply additional filters
    applyFilters()

    // Sort by dueAt by default
    sortTasksByDueAt()
  } catch (error) {
    console.error('Error loading tasks:', error)
    tasks.value = []
  } finally {
    loading.value = false
  }
}

// Apply filters to tasks
const applyFilters = () => {
  if (props.status) {
    tasks.value = tasks.value.filter((task) => task.status === props.status)
  }

  if (props.dueDate) {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const oneWeekFromNow = new Date(today)
    oneWeekFromNow.setDate(today.getDate() + 7)
    const oneMonthFromNow = new Date(today)
    oneMonthFromNow.setMonth(today.getMonth() + 1)

    tasks.value = tasks.value.filter((task) => {
      if (!task.dueAt) return props.dueDate === 'noDueDate'

      const dueDate = new Date(task.dueAt)

      switch (props.dueDate) {
        case 'overdue':
          return dueDate < today
        case 'today':
          return dueDate.toDateString() === today.toDateString()
        case 'thisWeek':
          return dueDate >= today && dueDate <= oneWeekFromNow
        case 'thisMonth':
          return dueDate >= today && dueDate <= oneMonthFromNow
        default:
          return true
      }
    })
  }

  if (props.search) {
    const searchTerm = props.search.toLowerCase()
    tasks.value = tasks.value.filter(
      (task) =>
        task.title.toLowerCase().includes(searchTerm) ||
        (task.description && task.description.toLowerCase().includes(searchTerm)),
    )
  }
}

// Sort tasks by dueAt
const sortTasksByDueAt = () => {
  tasks.value.sort((a, b) => {
    if (!a.dueAt) return 1
    if (!b.dueAt) return -1
    return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
  })
}

// Watch for prop changes to reload tasks
watch(
  () => [
    props.organizationId,
    props.personId,
    props.vehicleId,
    props.transactionId,
    props.assignedTo,
    props.status,
    props.dueDate,
    props.search,
  ],
  () => {
    loadTasks()
  },
)

// Add organization and person data fetching
const organization = ref<{ name: string } | null>(null)
const person = ref<{ firstName: string; lastName: string } | null>(null)

onMounted(async () => {
  loadTasks()

  // Fetch organization data if needed
  if (props.organizationId) {
    try {
      const org = await organizationStore.get(props.organizationId)
      if (org) {
        organization.value = org
      }
    } catch (error) {
      console.error('Error fetching organization:', error)
    }
  }

  // Fetch person data if needed
  if (props.personId) {
    try {
      const p = await personStore.get(props.personId)
      if (p) {
        person.value = p
      }
    } catch (error) {
      console.error('Error fetching person:', error)
    }
  }
})

// Task dialog methods
const openTaskDialog = () => {
  isEditing.value = false
  taskToEdit.value = undefined
  taskDialogVisible.value = true
}

const editTask = (task: Task) => {
  isEditing.value = true
  taskToEdit.value = task
  taskDialogVisible.value = true
}

const closeTaskDialog = () => {
  taskDialogVisible.value = false
}

const saveTask = async (taskData: Partial<Task>) => {
  try {
    if (isEditing.value && taskToEdit.value?.id) {
      await taskService.updateTask(taskToEdit.value.id, taskData)
    } else {
      // Ensure required fields are present
      if (!taskData.title) {
        console.error('Title is required')
        return
      }

      // Create a properly typed task data object
      const newTaskData = {
        ...taskData,
        organizationId: props.organizationId,
        personId: props.personId,
        vehicleId: props.vehicleId,
        transactionId: props.transactionId,
        tags: taskData.tags || [],
      }

      await taskService.createTask(newTaskData)
    }
    closeTaskDialog()
    loadTasks()
  } catch (error) {
    console.error('Error saving task:', error)
  }
}

// Delete methods
const confirmDeleteTask = (task: Task) => {
  taskToDelete.value = task
  deleteDialogVisible.value = true
}

const deleteTask = async () => {
  if (!taskToDelete.value) return

  try {
    await taskStore.remove(taskToDelete.value.id)
    deleteDialogVisible.value = false
    taskToDelete.value = null
    loadTasks()
  } catch (error) {
    console.error('Error deleting task:', error)
  }
}

// Helper methods
const getStatusSeverity = (status: string): string => {
  const map: Record<string, string> = {
    todo: 'info',
    doing: 'warning',
    done: 'success',
    cancelled: 'danger',
  }
  return map[status] || 'info'
}

// Navigation method
const navigateToEntity = (task: Task) => {
  if (task.personId) {
    router.push(`/crm/people/${task.personId}`)
  } else if (task.organizationId) {
    router.push(`/crm/organizations/${task.organizationId}`)
  }
}

// Add task grouping and color logic
const tasksByDueDate = computed(() => {
  const grouped = new Map<string, number>()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  taskStore.items.forEach((task) => {
    if (task.dueAt) {
      const dueDate = new Date(task.dueAt)
      dueDate.setHours(0, 0, 0, 0)

      // Only include tasks due today or in the future
      if (dueDate >= today) {
        const dateStr = dueDate.toISOString().split('T')[0]
        grouped.set(dateStr, (grouped.get(dateStr) || 0) + 1)
      }
    }
  })

  return grouped
})
</script>

<style scoped>
.confirmation-content {
  display: flex;
  align-items: center;
  padding: 1rem 0;
}
</style>
